import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiPlay, FiPause, FiVolume2, FiAlertCircle } = FiIcons;

function App() {
  const [activeButton, setActiveButton] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioSupported, setAudioSupported] = useState(true);
  const [safariWarning, setSafariWarning] = useState(false);
  const audioContextRef = useRef(null);
  const oscillator1Ref = useRef(null);
  const oscillator2Ref = useRef(null);
  const gainNodeRef = useRef(null);

  const beatConfigs = [
    {
      id: 1,
      beats: 7,
      label: '7 Batidos por Segundo',
      color: 'from-slate-800 to-slate-700',
      borderColor: 'border-slate-600'
    },
    {
      id: 2,
      beats: 9,
      label: '9 Batidos por Segundo',
      color: 'from-slate-800 to-blue-900',
      borderColor: 'border-blue-700'
    },
    {
      id: 3,
      beats: 11,
      label: '11 Batidos por Segundo',
      color: 'from-blue-900 to-slate-700',
      borderColor: 'border-blue-600'
    },
    {
      id: 4,
      beats: 14,
      label: '14 Batidos por Segundo',
      color: 'from-blue-800 to-slate-700',
      borderColor: 'border-blue-500'
    }
  ];

  // Detectar Safari iOS
  const isSafariIOS = () => {
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua);
    const webkit = /WebKit/.test(ua);
    const safari = /Safari/.test(ua) && !/Chrome/.test(ua);
    return iOS && webkit && safari;
  };

  useEffect(() => {
    // Verificar compatibilidad de audio
    const checkAudioSupport = () => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) {
          setAudioSupported(false);
          return;
        }

        // Detectar Safari iOS y mostrar advertencia
        if (isSafariIOS()) {
          setSafariWarning(true);
        }

        // Crear AudioContext de prueba
        const testContext = new AudioContext();
        testContext.close();
        setAudioSupported(true);
      } catch (error) {
        console.error('Audio no soportado:', error);
        setAudioSupported(false);
      }
    };

    checkAudioSupport();

    // Cleanup al desmontar
    return () => {
      stopCurrentSound();
    };
  }, []);

  const initializeAudioContext = async () => {
    try {
      if (!audioContextRef.current) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext();
      }

      // Safari iOS requiere que el AudioContext se inicie tras interacción del usuario
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      return audioContextRef.current;
    } catch (error) {
      console.error('Error inicializando AudioContext:', error);
      throw error;
    }
  };

  const stopCurrentSound = () => {
    try {
      if (oscillator1Ref.current) {
        oscillator1Ref.current.stop();
        oscillator1Ref.current.disconnect();
        oscillator1Ref.current = null;
      }
      if (oscillator2Ref.current) {
        oscillator2Ref.current.stop();
        oscillator2Ref.current.disconnect();
        oscillator2Ref.current = null;
      }
      if (gainNodeRef.current) {
        gainNodeRef.current.disconnect();
        gainNodeRef.current = null;
      }
    } catch (error) {
      console.error("Error deteniendo sonido:", error);
    }
    setIsPlaying(false);
  };

  const generateBeats = async (beatsPerSecond) => {
    try {
      // Inicializar AudioContext
      const audioContext = await initializeAudioContext();

      // Verificar que el contexto esté corriendo
      if (audioContext.state !== 'running') {
        throw new Error('AudioContext no está ejecutándose');
      }

      // Crear nodo de ganancia para control de volumen
      gainNodeRef.current = audioContext.createGain();
      
      // Volumen más alto para Safari iOS
      const volume = isSafariIOS() ? 0.5 : 0.3;
      gainNodeRef.current.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNodeRef.current.connect(audioContext.destination);

      // Frecuencias base
      const baseFreq = 440; // A4
      const beatFreq = beatsPerSecond;
      const freq1 = baseFreq;
      const freq2 = baseFreq + beatFreq;

      // Crear primer oscilador
      oscillator1Ref.current = audioContext.createOscillator();
      oscillator1Ref.current.type = 'sine';
      oscillator1Ref.current.frequency.setValueAtTime(freq1, audioContext.currentTime);
      oscillator1Ref.current.connect(gainNodeRef.current);

      // Crear segundo oscilador
      oscillator2Ref.current = audioContext.createOscillator();
      oscillator2Ref.current.type = 'sine';
      oscillator2Ref.current.frequency.setValueAtTime(freq2, audioContext.currentTime);
      oscillator2Ref.current.connect(gainNodeRef.current);

      // Configurar eventos de finalización para Safari iOS
      oscillator1Ref.current.onended = () => {
        if (oscillator1Ref.current) {
          oscillator1Ref.current = null;
        }
      };

      oscillator2Ref.current.onended = () => {
        if (oscillator2Ref.current) {
          oscillator2Ref.current = null;
        }
      };

      // Iniciar osciladores
      const startTime = audioContext.currentTime;
      oscillator1Ref.current.start(startTime);
      oscillator2Ref.current.start(startTime);

      setIsPlaying(true);

      // En Safari iOS, a veces necesitamos un pequeño delay
      if (isSafariIOS()) {
        setTimeout(() => {
          if (audioContext.state === 'suspended') {
            audioContext.resume();
          }
        }, 100);
      }

    } catch (error) {
      console.error("Error generando batidos:", error);
      stopCurrentSound();
      
      // Mostrar error específico para Safari iOS
      if (isSafariIOS()) {
        alert('Error de audio en Safari iOS. Asegúrate de que el volumen esté activado y prueba nuevamente.');
      }
    }
  };

  const handleButtonClick = async (config) => {
    // Verificar soporte de audio
    if (!audioSupported) {
      alert('Tu navegador no soporta la API de Audio Web necesaria para esta aplicación.');
      return;
    }

    try {
      // Si este botón está activo y reproduciéndose, detenerlo
      if (activeButton === config.id && isPlaying) {
        stopCurrentSound();
        setActiveButton(null);
      } else {
        // Detener cualquier sonido actual primero
        stopCurrentSound();
        
        // Establecer el nuevo botón activo
        setActiveButton(config.id);
        
        // Generar nuevos batidos
        await generateBeats(config.beats);
      }
    } catch (error) {
      console.error('Error en handleButtonClick:', error);
      setActiveButton(null);
      setIsPlaying(false);
    }
  };

  // Obtener versión de la aplicación
  const appVersion = import.meta.env.VITE_APP_ENV === 'production' ? '1.0.0' : '1.0.0-dev';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      {/* Advertencia para Safari iOS */}
      {safariWarning && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-orange-600 text-white p-3 text-center text-sm"
        >
          <SafeIcon icon={FiAlertCircle} className="inline mr-2" />
          Safari iOS detectado: Asegúrate de que el volumen esté activado y el modo silencioso desactivado
        </motion.div>
      )}

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center py-8 px-4"
      >
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          Escuela de Tecnología Pianística de Buenos Aires
        </h1>
        <div className="w-32 h-1 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full"></div>
      </motion.div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">
            Generador de Batidos Acústicos
          </h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
            Experimenta con diferentes frecuencias de batidos acústicos. Cada botón genera un patrón único de interferencia sonora que puede ayudar en la concentración y el entrenamiento auditivo.
          </p>
        </motion.div>

        {/* Error de soporte de audio */}
        {!audioSupported && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-red-600 text-white p-4 rounded-lg mb-8 text-center max-w-2xl mx-auto"
          >
            <SafeIcon icon={FiAlertCircle} className="text-2xl mb-2" />
            <p>Tu navegador no soporta la API de Audio Web necesaria para esta aplicación.</p>
            <p className="text-sm mt-2">Prueba con Chrome, Firefox, Safari o Edge actualizados.</p>
          </motion.div>
        )}

        {/* Beat Buttons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {beatConfigs.map((config, index) => (
            <motion.div
              key={config.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              className="flex justify-center"
            >
              <motion.button
                onClick={() => handleButtonClick(config)}
                disabled={!audioSupported}
                className={`
                  relative w-full max-w-xs p-8 rounded-2xl shadow-2xl text-white font-semibold transition-all duration-300 transform border
                  ${!audioSupported 
                    ? 'bg-gray-600 cursor-not-allowed opacity-50' 
                    : activeButton === config.id && isPlaying
                      ? `bg-gradient-to-br ${config.color} scale-105 shadow-3xl ${config.borderColor} border-2`
                      : `bg-gradient-to-br ${config.color} hover:scale-102 ${config.borderColor} border`
                  }
                `}
                whileHover={audioSupported ? { scale: 1.02 } : {}}
                whileTap={audioSupported ? { scale: 0.98 } : {}}
              >
                <div className="flex flex-col items-center space-y-4">
                  <div className={`
                    p-4 rounded-full
                    ${activeButton === config.id && isPlaying
                      ? 'bg-white bg-opacity-20'
                      : 'bg-slate-600 bg-opacity-40'
                    }
                  `}>
                    <SafeIcon
                      icon={activeButton === config.id && isPlaying ? FiPause : FiPlay}
                      className="text-2xl"
                    />
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2">{config.beats} BPS</div>
                    <div className="text-sm opacity-90">{config.label}</div>
                  </div>
                </div>

                {/* Efecto de pulso animado cuando está activo */}
                {activeButton === config.id && isPlaying && (
                  <motion.div
                    className="absolute inset-0 rounded-2xl border-2 border-white opacity-50"
                    animate={{
                      scale: [1, 1.05, 1],
                      opacity: [0.5, 0.2, 0.5],
                    }}
                    transition={{
                      duration: 60 / config.beats / 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                )}
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* Instructions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="bg-slate-800 bg-opacity-50 backdrop-blur-sm rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <SafeIcon icon={FiVolume2} className="text-2xl text-blue-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">
                Instrucciones de Uso
              </h3>
            </div>
            <div className="text-gray-300 space-y-3">
              <p>• Haz clic en cualquier botón para generar batidos acústicos a la frecuencia especificada</p>
              <p>• Los batidos se crean mediante la interferencia de dos ondas sinusoidales de frecuencias cercanas</p>
              <p>• Usa auriculares para una mejor experiencia auditiva</p>
              <p>• Haz clic nuevamente en el botón activo para detener el sonido</p>
              {isSafariIOS() && (
                <p className="text-orange-300">
                  • <strong>Safari iOS:</strong> Asegúrate de desactivar el modo silencioso y aumentar el volumen
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Version info */}
        <div className="text-center mt-8 text-gray-400 text-xs">
          <p>Versión {appVersion}</p>
          {isSafariIOS() && <p>Optimizado para Safari iOS</p>}
        </div>
      </div>
    </div>
  );
}

export default App;